import _ from 'lodash';
import { DefaultPmpManager } from 'core/pmp/PmpManager';
import {
  DefaultRtbCampaignBasicFormModel,
  RtbCampaignBasicFormModel,
  RtbCampaignBasicFormModelConstructorParams
} from './RtbCampaignBasicFormModel';
import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { DefaultAgencyManager } from 'core';
import { Pmp } from 'core/pmp/Pmp';
import {
  RtbCampaign,
  RtbCampaignBasicFormData
} from 'core/rtbCampaign/RtbCampaign';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export type PmpCampaign = {
  basic: Partial<RtbCampaignBasicFormData>;
  limitations: RtbCampaign['limitations'];
};

export interface PmpFormModel extends RtbCampaignBasicFormModel {
  readonly canEditBudgetPlan: boolean;
  readonly canEditPriceModel: boolean;
  readonly pmpSpaceOptions: SelectOptions[];
  readonly pmpList: Pmp[];
  readonly pmp?: Pmp;
  setPmp(pmp: Pmp): void;
  getMinBudgetOfPmpCampaign(schedule: {
    startDate: string;
    endDate: string;
  }): number;
}

export abstract class AbstractPmpFormModel
  extends DefaultRtbCampaignBasicFormModel
  implements PmpFormModel
{
  pmpManager = new DefaultPmpManager();
  agencyManager = new DefaultAgencyManager();
  adRequestSourceManager = new DefaultAdRequestSourceManager();
  pmpSpaceOptions: SelectOptions[] = [];
  pmp?: Pmp;

  constructor(
    mode: 'create' | 'edit',
    public pmpList: Pmp[],
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(mode, ...args);
  }

  override get canCreateWithCreative(): boolean {
    return true;
  }

  override async init(): Promise<void> {
    const { channelGroupId } = this.l1Object;
    // ECOPD-5566：停用渠道不支援 PMP
    if (
      ChannelGroupSetting.activeValues().filter(
        (setting) =>
          setting.config.addonFeatures.campaign.supportPMP &&
          setting.config.id === channelGroupId
      ).length === 0
    ) {
      console.error('current channel does not support PMP:', channelGroupId);
      return;
    }
    try {
      this.pmpSpaceOptions =
        await this.adRequestSourceManager.getSpacesWithConditions(
          true,
          {},
          'Outdoor',
          channelGroupId
        );
      await this.agencyManager.getAgencyName(this.order.agencyId);
    } catch (e) {
      console.error(e);
    }
  }

  override get isPmpType(): boolean {
    return true;
  }

  override get campaignAdType() {
    return 'PMP';
  }

  override get onlyBidCap(): boolean {
    return true;
  }

  setPmp(pmp: Pmp): void {
    this.pmp = pmp;
  }

  /**
   * 對齊後端 CampaignCreateValidator.validateBudget：門檻由 autoOptimise 決定。
   * 天數不先 startOf/endOf day，後端 ChronoUnit.DAYS.between 同樣吃實際時分秒，
   * 對齊整日會讓前端門檻偏高而誤擋。
   */
  getMinBudgetOfPmpCampaign(schedule: {
    startDate: string;
    endDate: string;
  }): number {
    const { budgetMinimum, campaignBudgetMinimum } =
      this.order.campaignConstraint;
    if (!this.l1Object.autoOptimise) {
      return campaignBudgetMinimum;
    }
    // 後端是 BigDecimal，這裡是浮點數：1.1 × 3 天 = 3.3000000000000003，
    // 會把剛好達標的活動誤判成不足。收掉尾巴，不是做幣別捨入。
    return _.round(
      this.rtbCampaignManager.getMinBudgetOfCampaign(schedule, budgetMinimum),
      10
    );
  }
}

export class EditPmpFormModel extends AbstractPmpFormModel {
  constructor(
    pmpList: Pmp[],
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super('edit', pmpList, ...args);
  }
  get canEditBudgetPlan(): boolean {
    return false;
  }
  get canEditPriceModel(): boolean {
    return false;
  }
  canEditOptimize(): boolean {
    return false;
  }
}

export class CreatePmpFormModel extends AbstractPmpFormModel {
  constructor(
    pmpList: Pmp[],
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super('create', pmpList, ...args);
  }
  get canEditBudgetPlan(): boolean {
    return true;
  }
  get canEditPriceModel(): boolean {
    return true;
  }
}
